import { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext.tsx';
import AdminRoute from './pages/Admin/AdminRoute.tsx';
import AdminPanel from './pages/Admin/AdminPanel.tsx';
import HomeLanding from './pages/Home/HomeLanding.tsx';
import Login from '../src/pages/Login/Login.tsx';
import RegisterCustomer from '../src/pages/Clientes/RegistrarClientes';
import ListarClientes from './pages/Clientes/ListarClientes.tsx';
import DetalleCliente from './pages/Clientes/DetalleCliente.tsx';
import Ariba from './pages/Ariba/AribaHome.tsx';
import BTP from './pages/SAP-BTP/BTPHome.tsx';
import IntegrationSuiteHome from './pages/SAP-BTP/IntegrationSuiteHome.tsx';
import RepositorioIflows from './pages/SAP-BTP/RepositorioIflows.tsx';
import CloudIdentityHome from './pages/SAP-BTP/CloudIdentityHome.tsx';
import GeminiChat from './components/ia/GeminiChat.tsx';
import AribaApis from './pages/Ariba/AribaApis.tsx';
import EnvioDocumentos from './pages/Ariba/sections/EnvioDocumentos.tsx';
import ValidadorEstructura from './pages/views/ValidadorEstructura.tsx';
import BadiCatalogToolbox from './pages/Ariba/sections/BadiCatalogToolbox.tsx';
import TablaCatalogToolbox from './pages/Ariba/sections/TablaCatalogToolbox.tsx';
import TransactionCatalogToolbox from './pages/Ariba/sections/TransactionCatalogToolbox.tsx';
import InterfaceCatalogToolbox from './pages/Ariba/sections/InterfaceCatalogToolbox.tsx';
import KnownErrorsCatalogToolbox from './pages/Ariba/sections/KnownErrorsCatalogToolbox.tsx';
import GuiasIntegracionToolbox from './pages/Ariba/sections/GuiasIntegracionToolbox.tsx';
import RequireAuth from './components/auth/RequireAuth.tsx';
import Navbar from './components/layout/Navbar.tsx';
import AppShell from './components/layout/AppShell.tsx';
import { ProjectProvider } from './context/ProjectContext.tsx';
import MisProyectos from './pages/Proyectos/MisProyectos.tsx';
import CrearProyecto from './pages/Proyectos/CrearProyecto.tsx';
import ProjectShell from './pages/Dashboard/HomeCA.tsx';
import CambiarPasswordObligatorio from './pages/Auth/CambiarPasswordObligatorio.tsx';
import ActivarCuenta from './pages/Auth/ActivarCuenta.tsx';
import PrivacidadExtension from './pages/Legal/PrivacidadExtension.tsx';
import MiPerfil from './pages/Perfil/MiPerfil.tsx';
import SolicitudesCredenciales from './pages/Credenciales/SolicitudesCredenciales.tsx';
import MensajesHome from './pages/Mensajes/MensajesHome.tsx';
import SoporteHome from './pages/Soporte/SoporteHome.tsx';
import AyudaHome from './pages/Soporte/AyudaHome.tsx';
import TicketsPorAtender from './pages/Soporte/TicketsPorAtender.tsx';
import MiEspacioHome from './pages/MiEspacio/MiEspacioHome.tsx';
import PruebasApisHome from './pages/APIs/PruebasApisHome.tsx';
import S4HanaApis from './pages/APIs/S4HanaApis.tsx';
import DocusignApis from './pages/APIs/DocusignApis.tsx';
import AribaApiTester from './pages/APIs/AribaApiTester.tsx';
import { EMPTY_API_CREDENTIALS } from './pages/APIs/apiCredentials.types.ts';

const CAMBIAR_PASSWORD_PATH = '/cambiar-password';

export default function App() {
  const { user, profile } = useAuth();
  const location = useLocation();

  // 🔒 Choke point único: si la cuenta requiere cambio de contraseña
  // (creada por otro miembro vía createProjectMember), no se deja avanzar
  // a ninguna otra ruta —ni operativa, ni admin, ni proyectos— hasta
  // resolverlo. Vive acá arriba de <Routes> para cubrir todas las rutas
  // de una sola vez.
  if (profile?.requiereCambioPassword && location.pathname !== CAMBIAR_PASSWORD_PATH) {
    return <Navigate to={CAMBIAR_PASSWORD_PATH} replace />;
  }

  // Nexus (el asistente IA) vive acá arriba de <Routes>, no dentro de
  // OperativeApp, para que sea un widget GLOBAL: la misma instancia
  // persiste (historial de Firestore + estado abierto/cerrado) sin
  // importar a qué ruta se navegue. Se excluye /admin/* a propósito
  // (panel operativo aparte, sin necesidad del asistente ahí por ahora).
  const ocultarOneBot = location.pathname.startsWith('/admin');

  return (
    <>
      <Routes>
      <Route
        path={CAMBIAR_PASSWORD_PATH}
        element={<RequireAuth><CambiarPasswordObligatorio /></RequireAuth>}
      />

      {/* Invitaciones: el correo de bienvenida lleva acá para crear la contraseña (pública, sin sesión). */}
      <Route path="/activar" element={<ActivarCuenta />} />
      {/* Pública: la URL de la política va en la Chrome Web Store. */}
      <Route path="/privacidad-extension" element={<PrivacidadExtension />} />

      {/* 🔒 Área de Administración: layout y protección de ruta separados del portal operativo */}
      <Route path="/admin/*" element={<AdminRoute><AdminPanel /></AdminRoute>} />

      {/* 🗂️ Proyecto como entidad central: todo el progreso/checklist de
          integración (Cloud Connector, Orden de Compra, Cutover, etc.)
          vive anidado bajo /proyecto/:projectId. Estas rutas viven fuera
          del switch de páginas de OperativeApp, así que usan AppShell
          para conservar el mismo fondo oscuro y navbar del resto de la
          plataforma (si no, se renderizan "desnudas" sin navegación). */}
      <Route
        path="/perfil"
        element={<RequireAuth><AppShell><MiPerfil /></AppShell></RequireAuth>}
      />
      {/* Obtener credenciales con aprobación (docs/plans/plan-revelado-credenciales.md): los correos llevan acá. */}
      <Route
        path="/solicitudes-credenciales"
        element={<RequireAuth><AppShell><SolicitudesCredenciales /></AppShell></RequireAuth>}
      />
      <Route
        path="/mi-espacio"
        element={<RequireAuth><AppShell><MiEspacioHome /></AppShell></RequireAuth>}
      />
      <Route
        path="/mensajes"
        element={<RequireAuth><AppShell activePage="mensajes"><MensajesHome /></AppShell></RequireAuth>}
      />
      <Route
        path="/soporte"
        element={<RequireAuth><AppShell activePage="soporte"><SoporteHome /></AppShell></RequireAuth>}
      />
      <Route
        path="/ayuda"
        element={<RequireAuth><AppShell activePage="ayuda"><AyudaHome /></AppShell></RequireAuth>}
      />
      <Route
        path="/tickets-por-atender"
        element={<RequireAuth><AppShell activePage="proyectos"><TicketsPorAtender /></AppShell></RequireAuth>}
      />
      <Route
        path="/clientes"
        element={<RequireAuth><AppShell activePage="clientes"><ListarClientes /></AppShell></RequireAuth>}
      />
      <Route
        path="/clientes/nuevo"
        element={<RequireAuth><AppShell activePage="clientes"><RegisterCustomer /></AppShell></RequireAuth>}
      />
      <Route
        path="/clientes/:clienteId"
        element={<RequireAuth><AppShell activePage="clientes"><DetalleCliente /></AppShell></RequireAuth>}
      />
      <Route
        path="/proyectos"
        element={<RequireAuth><AppShell activePage="proyectos"><MisProyectos /></AppShell></RequireAuth>}
      />
      <Route
        path="/proyectos/nuevo"
        element={<RequireAuth><AppShell activePage="proyectos"><CrearProyecto /></AppShell></RequireAuth>}
      />
      <Route
        path="/proyecto/:projectId/*"
        element={
          <RequireAuth>
            <AppShell activePage="proyectos">
              <ProjectProvider>
                <ProjectShell />
              </ProjectProvider>
            </AppShell>
          </RequireAuth>
        }
      />

      <Route path="*" element={<OperativeApp />} />
    </Routes>
    {user && !ocultarOneBot && <GeminiChat user={user} />}
    </>
  );
}

function OperativeApp() {
  const [page, setPage] = useState('home');
  const { user, loading } = useAuth();
  const [showLogin, setShowLogin] = useState(false); // 👈 control login modal
  // Credenciales del hub "Pruebas APIs" fuera de un proyecto: viven acá
  // (no en cada sub-página) porque el usuario puede ir y volver entre el
  // hub y S/4HANA/Docusign sin perder lo que ya escribió.
  const [apiCredentials, setApiCredentials] = useState(EMPTY_API_CREDENTIALS);

  if (loading) return <div className="text-white p-10">Loading...</div>;

  // 🔒 Función para proteger vistas
  const requireAuth = (callback) => {
    if (!user) {
      setShowLogin(true);
    } else {
      callback();
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">

      <Navbar
        activePage={page === 'home' ? 'home' : undefined}
        onHomeClick={() => setPage('home')}
        requireAuth={requireAuth}
      />

      {/* 🔹 HOME (PÚBLICO) */}

      {page === 'ariba' && (
        <Ariba requireAuth={requireAuth} setPage={setPage} />
      )}
      {page === 'btp' && (
        <BTP requireAuth={requireAuth} setPage={setPage} />
      )}

      {page === 'home' && (
        <HomeLanding requireAuth={requireAuth} setPage={setPage} />
      )}

      {/* 🔒 PROTEGIDOS */}
      {page === 'btp-integration-suite' && user && <IntegrationSuiteHome setPage={setPage} />}
      {page === 'btp-iflows' && user && <RepositorioIflows setPage={setPage} />}
      {page === 'btp-cloud-identity' && user && <CloudIdentityHome setPage={setPage} />}
      {page === 'ariba-apis' && user && <AribaApis setPage={setPage} />}
      {page === 'cig-send' && user && <EnvioDocumentos setPage={setPage} />}
      {page === 'validador-estructura' && user && <ValidadorEstructura setPage={setPage} />}
      {page === 'badis-toolbox' && user && <BadiCatalogToolbox setPage={setPage} />}
      {page === 'tablas-toolbox' && user && <TablaCatalogToolbox setPage={setPage} />}
      {page === 'transacciones-toolbox' && user && <TransactionCatalogToolbox setPage={setPage} />}
      {page === 'interfaces-toolbox' && user && <InterfaceCatalogToolbox setPage={setPage} />}
      {page === 'errores-conocidos-toolbox' && user && <KnownErrorsCatalogToolbox setPage={setPage} />}
      {page === 'guias-integracion-toolbox' && user && <GuiasIntegracionToolbox setPage={setPage} />}
      {page === 'pruebas-apis' && user && (
        <PruebasApisHome setPage={setPage} credentials={apiCredentials} onCredentialsChange={setApiCredentials} />
      )}
      {page === 's4-apis' && user && <S4HanaApis setPage={setPage} credentials={apiCredentials} />}
      {page === 'docusign-apis' && user && <DocusignApis setPage={setPage} credentials={apiCredentials} />}
      {page === 'ariba-api-test' && user && <AribaApiTester setPage={setPage} credentials={apiCredentials} />}


      {/* 🔐 LOGIN MODAL */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center">
          <div className="bg-gray-800 p-6 rounded-xl">
            <Login
              onLogin={() => {
                // El estado de `user`/`profile` se actualiza solo vía AuthContext
                // (onAuthStateChanged), aquí solo cerramos el modal.
                setShowLogin(false);
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
}